import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Gauge, Loader2 } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import {
  fetchTenantRegistrationStatus,
  isTerminalRegistrationStatus,
  TenantRegistrationApiError,
} from '@/services/tenantRegistrationClient';

const POLL_MS = 8000;

export default function TenantRegistrationStatusPage() {
  const { registrationId } = useParams();
  const [searchParams] = useSearchParams();
  const { state: locationState } = useLocation();
  const token =
    searchParams.get('token') || searchParams.get('status_token') || '';
  const { t, lang, pathFor, toggleLang } = useLanguage();
  const rs = t.registrationStatus;

  const [status, setStatus] = useState('');
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fromSubmit = locationState?.derived_schema_preview;
    if (fromSubmit) setPreview(String(fromSubmit));
  }, [locationState]);

  useEffect(() => {
    if (!registrationId || !token) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    /** @type {ReturnType<typeof setInterval> | undefined} */
    let intervalId;

    const tick = async () => {
      try {
        const data = await fetchTenantRegistrationStatus(registrationId, token);
        if (cancelled) return;
        const s = data?.status != null ? String(data.status) : '';
        setStatus(s);
        if (data?.derived_schema_preview) {
          setPreview(String(data.derived_schema_preview));
        }
        setError('');
        if (isTerminalRegistrationStatus(s) && intervalId != null) {
          clearInterval(intervalId);
          intervalId = undefined;
        }
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof TenantRegistrationApiError
            ? e.message
            : (e?.message || rs.loadError);
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    tick();
    intervalId = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (intervalId != null) clearInterval(intervalId);
    };
  }, [registrationId, token, rs.loadError]);

  const missing = !registrationId || !token;

  return (
    <div className="min-h-screen flex" data-testid="registration-status-page">
      <div className="hidden lg:flex lg:w-[52%] bg-[#0D0303] flex-col p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 1px,transparent 48px),repeating-linear-gradient(90deg,#fff 0,#fff 1px,transparent 1px,transparent 48px)',
          }}
        />
        <div className="absolute top-0 left-0 w-1 h-full bg-[#A11212]" />
        <Link to={pathFor('/')} className="flex items-center gap-3 relative z-10">
          <div className="w-9 h-9 bg-[#A11212] flex items-center justify-center">
            <Gauge className="w-5 h-5 text-white" />
          </div>
          <span
            className="font-black text-xl text-white tracking-tight"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            CarniTrack
          </span>
        </Link>
        <div className="flex-1 flex flex-col justify-center relative z-10 max-w-sm">
          <div className="w-8 h-[3px] bg-[#A11212] mb-8" />
          <h2
            className="text-3xl xl:text-4xl font-black text-white leading-tight tracking-tighter mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            {rs.heroTitle}
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed">{rs.heroSub}</p>
        </div>
      </div>

      <div className="flex-1 bg-white flex flex-col items-center justify-center px-8 py-12 relative">
        <Link to={pathFor('/')} className="flex items-center gap-2 mb-8 lg:hidden">
          <div className="w-8 h-8 bg-[#A11212] flex items-center justify-center">
            <Gauge className="w-4 h-4 text-white" />
          </div>
          <span
            className="font-black text-lg text-gray-900"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            CarniTrack
          </span>
        </Link>

        <div className="w-full max-w-md text-center">
          <h1
            className="text-2xl font-black text-gray-900 tracking-tighter mb-2"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            {rs.title}
          </h1>

          {missing && (
            <p className="text-sm text-red-600 mb-6">{rs.missingLink}</p>
          )}

          {!missing && loading && !error && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="w-10 h-10 text-[#A11212] animate-spin" />
              <p className="text-sm text-gray-500">{rs.loading}</p>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 mb-6" data-testid="status-error">
              {error}
            </p>
          )}

          {!missing && !loading && !error && (
            <div className="space-y-6 text-left">
              {preview && (
                <div className="border border-gray-200 bg-gray-50 p-4 text-sm">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {rs.workspacePreview}
                  </p>
                  <p className="font-mono text-gray-900">{preview}</p>
                </div>
              )}

              <div className="border border-gray-200 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                  {rs.statusLabel}
                </p>
                <p className="text-lg font-semibold text-gray-900 capitalize" data-testid="registration-status-value">
                  {status || rs.unknown}
                </p>
              </div>

              {String(status).toLowerCase() === 'pending' && (
                <p className="text-sm text-gray-600">{rs.pendingHint}</p>
              )}

              {String(status).toLowerCase() === 'approved' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">{rs.approvedHint}</p>
                  <Link
                    to={pathFor('/signin')}
                    className="inline-flex w-full h-11 items-center justify-center bg-[#A11212] hover:bg-[#8B0F0F] text-white font-bold uppercase tracking-wider text-xs transition-colors"
                    data-testid="status-signin-link"
                  >
                    {rs.signInCta}
                  </Link>
                </div>
              )}

              {String(status).toLowerCase() === 'rejected' && (
                <p className="text-sm text-red-700">{rs.rejectedHint}</p>
              )}
            </div>
          )}

          <p className="text-sm text-gray-500 mt-8">
            <Link to={pathFor('/setup')} className="text-[#A11212] font-semibold hover:underline">
              {rs.backSetup}
            </Link>
          </p>
        </div>

        <button
          type="button"
          onClick={toggleLang}
          className="absolute bottom-6 right-6 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 px-3 py-1 transition-colors"
        >
          {lang === 'EN' ? 'Türkçe' : 'English'}
        </button>
      </div>
    </div>
  );
}
